import { Maybe } from 'graphql/jsutils/Maybe'
import { NextPage } from 'next'
import { paths } from '@/constants/paths'
import { ProjectMetaProvider } from '@/contexts/ProjectMetaContext'
import { Seo } from '@/layout/Seo'
import { TVSeriesJsonLd } from '@/layout/Seo/JsonLd'
import { getJsonLdForTVSeries, useMetaForProject } from '@/layout/Seo/ProjectSeoHelper'
import { GetAppBannerProvider } from '@/organisms/GetAppBanner'
import { ShopifyConfigProvider } from '@/organisms/HorizontalShopifyMerchScroller/ShopifyConfigContext'
import { getPageByPath } from '@/services/CmsService/Pages'
import { createWatchProjectDeepLink } from '@/services/DeepLinkService/DeepLinkService'
import { getFeatureAnnouncement } from '@/services/FeatureAnnouncementService'
import { ProjectMeta, getProjectAbout } from '@/services/ProjectsService'
import { fetchPageDataContext, PageDataContext, PageModel } from '@/services/RenderService'
import { getShopifyConfigurationForProject } from '@/services/ShopifyConfigurationService'
import { ShopifyConfiguration } from '@/services/ShopifyConfigurationService/ShopifyConfigurationService'
import { WatchlistProvider } from '@/services/WatchlistService/WatchlistProvider'
import { Page } from '@/types/codegen-contentful'
import { getStaticPropsErrorHandler } from '@/utils/nextUtils/nextErrorHandlers'
import { determinePageRevalidation } from '@/utils/nextUtils/revalidation'
import { getGenericStaticPaths } from '@/utils/nextUtils/staticPages'
import { loadTranslations } from '@/utils/translate/translate-server'
import { DryBarProjectView } from '@/views/WatchProjectView'

interface DryBarProjectPageProps {
  locale: string
  deepLink: string | null
  watchProjectPage: Page | null
  projectSlug: string
  pageDataContext: Maybe<PageDataContext>
  page: Maybe<PageModel>
  shopifyConfig?: ShopifyConfiguration | null
}

export const WatchProjectPage: NextPage<DryBarProjectPageProps & ProjectMeta> = (props) => {
  const meta = useMetaForProject(props)

  return (
    <>
      <Seo {...meta} nofollow={!props.public} noindex={!props.public} path={`${paths.watch.index}/${props.slug}`} />
      <TVSeriesJsonLd {...getJsonLdForTVSeries(props)} />
      <ShopifyConfigProvider
        storeUrl={props.shopifyConfig?.storeUrl}
        accessToken={props.shopifyConfig?.accessToken}
        handle={props.shopifyConfig?.handle}
        storeHref={props.shopifyConfig?.storeHref}
      >
        <ProjectMetaProvider project={props}>
          <GetAppBannerProvider deepLink={props.deepLink} projectSlug={props.projectSlug} context="watch">
            <WatchlistProvider>
              <DryBarProjectView {...props} />
            </WatchlistProvider>
          </GetAppBannerProvider>
        </ProjectMetaProvider>
      </ShopifyConfigProvider>
    </>
  )
}

export const getStaticProps = getStaticPropsErrorHandler<DryBarProjectPageProps & ProjectMeta>(
  { filePath: 'pages/[locale]/watch/dry-bar-best-of.tsx' },
  async ({ locale, federationClient, preview, next404 }) => {
    const projectSlug = 'dry-bar-best-of'

    const projectMeta = await getProjectAbout({ slug: projectSlug }, federationClient, { locale, preview })
    if (!projectMeta) return next404()

    const [page, deepLink, shopifyConfig, translations] = await Promise.all([
      getPageByPath(`${paths.watch.index}/${projectSlug}`, { preview, locale }),
      createWatchProjectDeepLink(
        {
          path: `${paths.watch.index}/${projectSlug}`,
          projectSlug,
          projectName: projectMeta?.name,
          contentName: projectMeta?.name,
          audioLocale: locale,
          captionLocale: locale,
        },
        {
          campaign: 'prompt-app-install',
          feature: 'app-prompt',
          channel: 'angel-web',
        },
      ),
      getShopifyConfigurationForProject(projectSlug, { locale, preview }),
      loadTranslations(locale, [
        'common',
        'home',
        'watch',
        'app-promo',
        'ads',
        'account',
        'guild',
        'theatrical-presales',
      ]),
    ])

    const pageDataContext = page ? await fetchPageDataContext(page, preview, locale, federationClient) : null

    return {
      props: {
        locale,
        projectSlug,
        deepLink,
        shopifyConfig,
        ...projectMeta,
        featureAnnouncement: getFeatureAnnouncement(projectSlug),
        page,
        pageDataContext,
        watchProjectPage: page,
        ...translations,
      },
      revalidate: determinePageRevalidation(page, pageDataContext),
    }
  },
)

export const getStaticPaths = getGenericStaticPaths

export default WatchProjectPage
